import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Heart,
  Loader2,
  MapPin,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../hooks/useUsers';
import { useEnrichedMatches } from '../../hooks/useEnrichedMatches';
import { calculateCompatibilityScore, getBudgetOverlap } from '../../utils/compatibility';
import BottomNav from '../../components/BottomNav';
import TenantNav from '../../components/TenantNav';

const formatBudget = (value?: number) => {
  if (!value || Number.isNaN(value)) return 'R0';
  return `R${Math.floor(value / 1000)}k`;
};

const MatchProfile: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { user: currentUserData } = useUser(currentUser?.uid);
  const { enrichedMatches, loading } = useEnrichedMatches();

  const match = useMemo(
    () => enrichedMatches.find((item) => item.matchId === matchId),
    [enrichedMatches, matchId]
  );
  const matchedUser = match?.user;

  const compatibilityScore = useMemo(() => {
    if (currentUserData && matchedUser) {
      return calculateCompatibilityScore(currentUserData, matchedUser);
    }
    return match?.compatibilityScore || 0;
  }, [currentUserData, matchedUser, match?.compatibilityScore]);

  const budgetOverlap = useMemo(() => {
    if (currentUserData && matchedUser) {
      const overlap = getBudgetOverlap(currentUserData, matchedUser);
      if (overlap.max > overlap.min) return overlap;
    }
    return null;
  }, [currentUserData, matchedUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-3 sm:px-4 pt-4 pb-24 md:pb-10">
        <div className="max-w-5xl mx-auto space-y-4">
          <TenantNav />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-primary-300 animate-spin mx-auto mb-4" />
              <p className="text-text-secondary font-semibold">Loading profile...</p>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!matchId || !match || !matchedUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-3 sm:px-4 pt-4 pb-24 md:pb-10">
        <div className="max-w-4xl mx-auto space-y-4">
          <TenantNav />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="card p-6 md:p-8 text-center max-w-md">
              <Heart className="w-12 h-12 md:w-16 md:h-16 text-primary-300 mx-auto mb-4" />
              <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-2">Profile not available</h2>
              <p className="text-text-secondary mb-6 text-sm md:text-base">
                We could not find that match. Try refreshing your matches list.
              </p>
              <button className="btn-primary" onClick={() => navigate('/tenant/matches')}>
                Back to Matches
              </button>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  const budgetRange = `${formatBudget(matchedUser.budgetMin)} - ${formatBudget(matchedUser.budgetMax)}`;
  const sharedLifestyle = match.sharedLifestyle?.length ? match.sharedLifestyle : matchedUser.lifestyle || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-3 sm:px-4 pt-4 pb-24 md:pb-10">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-28 -left-20 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-5xl mx-auto space-y-5 md:space-y-6">
        <TenantNav />

        <div className="card p-4 md:p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <button
                onClick={() => navigate('/tenant/matches')}
                className="p-2 rounded-xl bg-white border border-gray-200 text-text-primary hover:border-primary-200 hover:text-primary-300 transition-colors"
                aria-label="Back to matches"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted font-semibold">Match profile</p>
                <h1 className="text-2xl md:text-3xl font-extrabold text-text-primary flex items-center gap-2">
                  {matchedUser.name}
                  {matchedUser.age ? (
                    <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-300 text-xs font-semibold">
                      {matchedUser.age} yrs
                    </span>
                  ) : null}
                </h1>
                <p className="text-text-secondary flex items-center gap-1.5 text-sm mt-1">
                  <MapPin className="w-4 h-4" />
                  {matchedUser.city || matchedUser.metro || 'Location unknown'} - {budgetRange}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1.5 rounded-full bg-primary-50 text-primary-300 border border-primary-100 text-xs md:text-sm font-semibold flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" /> {compatibilityScore}% compatibility
              </span>
              <span className="px-3 py-1.5 rounded-full bg-white border-2 border-gray-100 text-text-secondary text-xs md:text-sm font-semibold flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-primary-300" /> Active match
              </span>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-primary-50 border-2 border-primary-100">
              <p className="text-[11px] text-text-secondary flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Location
              </p>
              <p className="text-sm font-semibold text-text-primary mt-1">
                {matchedUser.metro || matchedUser.city || 'Not shared'}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-accent-blue/10 border-2 border-accent-blue/30">
              <p className="text-[11px] text-text-secondary flex items-center gap-1">
                <Wallet className="w-3 h-3" /> Budget
              </p>
              <p className="text-sm font-semibold text-text-primary mt-1">{budgetRange}</p>
              {budgetOverlap && (
                <p className="text-[11px] text-accent-blue mt-1">
                  Shared: {formatBudget(budgetOverlap.min)} - {formatBudget(budgetOverlap.max)}
                </p>
              )}
            </div>
            <div className="p-3 rounded-xl bg-white border-2 border-gray-100">
              <p className="text-[11px] text-text-secondary flex items-center gap-1">
                <Users className="w-3 h-3 text-primary-300" /> Vibe
              </p>
              <p className="text-sm font-semibold text-text-primary mt-1">
                {matchedUser.lifestyle?.[0] || 'Easy-going'}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => navigate(`/tenant/chat/${matchId}`)}
              className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm md:text-base py-3"
            >
              <MessageCircle className="w-4 h-4" /> Message {matchedUser.name.split(' ')[0]}
            </button>
            <button
              type="button"
              onClick={() => navigate('/tenant/matches')}
              className="btn-secondary flex-1 sm:flex-none flex items-center justify-center gap-2 text-sm md:text-base py-3"
            >
              <ArrowLeft className="w-4 h-4" /> Back to matches
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="card p-4 md:p-5 space-y-3">
            <div className="flex items-center gap-2 text-text-primary font-semibold">
              <Sparkles className="w-4 h-4 text-primary-300" /> About {matchedUser.name.split(' ')[0] || 'this match'}
            </div>
            <p className="text-text-secondary leading-relaxed text-sm md:text-base">
              {matchedUser.bio || 'No bio yet. Start a chat to learn more!'}
            </p>
            <div className="flex flex-wrap gap-2">
              {matchedUser.lifestyle?.map((item) => (
                <span
                  key={item}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white border-2 border-primary-100 text-text-primary"
                >
                  {item}
                </span>
              ))}
              {(!matchedUser.lifestyle || matchedUser.lifestyle.length === 0) && (
                <span className="text-xs text-text-muted">No lifestyle tags shared yet.</span>
              )}
            </div>
            {matchedUser.dailyRhythms?.length ? (
              <div>
                <p className="text-[11px] uppercase text-text-muted font-semibold mb-1.5">Daily rhythm</p>
                <div className="flex flex-wrap gap-2">
                  {matchedUser.dailyRhythms.map((rhythm) => (
                    <span
                      key={rhythm}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold bg-primary-50 border-2 border-primary-100 text-primary-300"
                    >
                      {rhythm}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="card p-4 md:p-5 space-y-3">
            <div className="flex items-center gap-2 text-text-primary font-semibold">
              <Users className="w-4 h-4 text-primary-300" /> Interests
            </div>
            <div className="flex flex-wrap gap-2">
              {matchedUser.interests?.length ? (
                matchedUser.interests.map((interest) => (
                  <span
                    key={interest}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-accent-purple/10 text-accent-purple border border-accent-purple/30"
                  >
                    {interest}
                  </span>
                ))
              ) : (
                <span className="text-xs text-text-muted">Interests will appear here once shared.</span>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-2 text-sm text-text-secondary">
              <div className="p-3 rounded-xl bg-white border-2 border-gray-100">
                <p className="text-[11px] uppercase text-text-muted font-semibold">Smoking</p>
                <p className="text-text-primary font-semibold mt-1 capitalize">
                  {matchedUser.smokingPreference?.replace('-', ' ') || 'Not shared'}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-white border-2 border-gray-100">
                <p className="text-[11px] uppercase text-text-muted font-semibold">Pets</p>
                <p className="text-text-primary font-semibold mt-1 capitalize">
                  {matchedUser.petsPreference?.replace('-', ' ') || 'Not shared'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card p-4 md:p-5 space-y-3">
          <div className="flex items-center gap-2 text-text-primary font-semibold">
            <ShieldCheck className="w-4 h-4 text-primary-300" /> Common ground
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-primary-50 border-2 border-primary-100">
              <p className="text-[11px] text-text-secondary">Shared city</p>
              <p className="text-sm font-semibold text-text-primary mt-1">{match.sharedCity || matchedUser.city || 'TBD'}</p>
            </div>
            <div className="p-3 rounded-xl bg-white border-2 border-gray-100">
              <p className="text-[11px] text-text-secondary">Shared lifestyle</p>
              {sharedLifestyle.length ? (
                <div className="flex flex-wrap gap-1 mt-1">
                  {sharedLifestyle.slice(0, 3).map((item) => (
                    <span
                      key={item}
                      className="px-2 py-1 rounded-full text-[11px] font-semibold bg-primary-50 text-primary-300 border border-primary-100"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-semibold text-text-primary mt-1">Swap a few lifestyle tags.</p>
              )}
            </div>
            <div className="p-3 rounded-xl bg-accent-blue/10 border-2 border-accent-blue/30">
              <p className="text-[11px] text-text-secondary">Match score</p>
              <p className="text-2xl font-extrabold text-text-primary mt-1">{compatibilityScore}%</p>
              <p className="text-[11px] text-text-secondary">Based on your shared signals.</p>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default MatchProfile;
